import useResizeObserver from "@react-hook/resize-observer";
import { useCallback, useEffect, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Tree } from "antd";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { getFilesFromDropEvent } from "utils/upload/folderDrop";

import { Heading } from "components/base";
import { IconSpinner } from "components/icons";
import { NodeProviderWithManager } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/NodeProviderWithManager";
import { ProjectTreeNodeT } from "components/project/layers/hooks";
import {
  MCDANIEL_SHAPEFILES_LABEL,
  ORGANIZATION_SHAPEFILES_LABEL,
  PROJECT_SHAPEFILES_LABEL,
  SHAPEFILE_FOLDER_TYPE
} from "components/project/layers/hooks/project-layer-tree/useProjectLayerTree.types";
import { findParentNode } from "components/project/layers/utils";
import { useProjectContext } from "components/project/projects/context";
import {
  useShapefileContext,
  useShapefileDispatch
} from "components/project/shapefiles/context";
import { useUploadProjectShapefileList } from "components/project/shapefiles/hooks/useUploadProjectShapefileList";
import {
  useMoveProjectShapefileFolderMutation,
  useMoveProjectShapefileMutation
} from "components/project/shapefiles/mutations";
import { useProjectShapefileFeaturesQuery } from "components/project/shapefiles/queries";

import { useOrgShapefiles } from "../hooks/useOrgShapefiles";
import { OrganizationShapefileDetails } from "./OrganizationShapefileDetails";
import "./OrganizationShapefiles.scss";
import { OrganizationTreeNodeManager } from "./OrganizationTreeNodeManager";

interface OrganizationShapefilesT {
  allowUpload?: boolean;
  showHeader?: boolean;
}
export default function OrganizationShapefiles({
  allowUpload = true,
  showHeader = true
}: OrganizationShapefilesT): JSX.Element {
  const { isTreeDraggable } = useProjectContext();
  const { isLoadingColors } = useShapefileContext();

  const [uploadError, setUploadError] = useState(false);
  const treeContainer = useRef(null);
  const [selectedShapefile, setSelectedShapefile] = useState(null);
  const [selectedFeatureProperty, setSelectedFeatureProperty] = useState({});
  const [shapefileFeatures, setShapefileFeatures] = useState({});
  const [, setTreeHeight] = useState(585);
  const shapefileDispatch = useShapefileDispatch();

  useResizeObserver(treeContainer, (client) => {
    setTreeHeight(client.contentRect.height);
  });
  const { tree } = useOrgShapefiles();

  const currentShapefileFeatureSortDirection = useSelector(
    (state: RootState) => state.project.currentShapefileFeatureSortDirection
  );

  const role = useSelector((state: RootState) => state.auth.user.role);
  const isAdminOrPowerUser = role === "Admin" || role === "PowerUser";

  const shapefileFeaturesQuery = useProjectShapefileFeaturesQuery({
    id: selectedShapefile?.key,
    isProjectLinkedShapefile: false,
    selectedFeatureProperty: selectedFeatureProperty[selectedShapefile?.key],
    sortDirection: currentShapefileFeatureSortDirection
  });

  useEffect(() => {
    if (!selectedShapefile?.shapefileId) return;
    if (shapefileFeaturesQuery.data) {
      const shapefile = shapefileFeaturesQuery.data;
      const newFeatures = shapefile.geoms;
      if (newFeatures) {
        const id = selectedShapefile.shapefileId;
        const features = {
          [id]: {
            features: newFeatures,
            layerName: selectedShapefile.name
          }
        };
        setShapefileFeatures({ ...shapefileFeatures, ...features });

        const property = selectedFeatureProperty[id]
          ? selectedFeatureProperty[id]
          : selectedShapefile.properties.find((x) => x === "NAME") ||
            selectedShapefile.properties[0];

        if (property) {
          setSelectedFeatureProperty({ ...selectedFeatureProperty, [id]: property });
        }
      }
    }
  }, [shapefileFeaturesQuery.data]);

  useEffect(() => {
    shapefileDispatch({
      payload: { isLoadingDetails: shapefileFeaturesQuery.isLoading }
    });
  }, [shapefileFeaturesQuery.isLoading]);

  useEffect(() => {
    shapefileFeaturesQuery.isError
      ? shapefileDispatch({
          payload: { error: "Failed to apply color palette to shapefile attributes" }
        })
      : shapefileDispatch({ payload: { error: "" } });
  }, [shapefileFeaturesQuery.isError]);

  const moveProjectShapefile = useMoveProjectShapefileMutation({
    projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION
  });

  const moveProjectShapefileFolder = useMoveProjectShapefileFolderMutation({
    projectId: SHAPEFILE_FOLDER_TYPE.ORGANIZATION
  });

  function getDropKey(dropKey) {
    if (dropKey === PROJECT_SHAPEFILES_LABEL || dropKey === MCDANIEL_SHAPEFILES_LABEL) {
      return "";
    }
    if (dropKey === ORGANIZATION_SHAPEFILES_LABEL) {
      return "orgLevelShapefiles";
    }
    return dropKey;
  }

  const onDrop = useCallback(
    (info) => {
      const dragType = info.dragNode.type;
      let dropKey = info.node.key;
      const dragKey = info.dragNode.key;
      let dropPosition = info.dropPosition;
      if (info.dropToGap) {
        const parentNodeId = findParentNode(tree, dropKey, "org");
        //drop to gap means that the user wishes the dragNode
        //to to be a sibiling of the drop target
        //the parent of the dropkey is the actual drop target
        dropKey = parentNodeId ?? "";
      } else {
        const dragParentNodeId = findParentNode(tree, dragKey, "");
        const dragNode = info.dragNode;
        const dropNode = info.node;
        const droppedToSameFolder =
          dragNode?.shapefileNodeId == dropNode.shapefileNodeId || //shapefileParentNode is same as folder
          dragNode?.parentId === dropNode.shapefileNodeId ||
          dragParentNodeId === dropNode.key; //parent node is same as drop node
        if (droppedToSameFolder) {
          //dropping to the same folder previously and dropToGap is false
          //so the user must want to drop to the top of the folder
          dropPosition = 0;
        }
      }

      if (info.node.type === "shapefile") {
        if (!info.dropToGap) {
          dropPosition += 1;
        }
        //can't drop on to a shapefile so we move it to the node that
        //holds the shapefile we're trying to drop to
        dropKey = info.node.shapefileNodeId;
      }

      const dropLevel = info.node.pos.split("-").length;
      const dropToRoot = dropLevel === 2;

      if (dropToRoot) {
        //dropped to root node so set dropPosition to 0 because
        //user wants to drop it at the top of the
        dropPosition = 0;
      }
      if (dragType === "node") {
        dropKey = getDropKey(dropKey);
        moveProjectShapefileFolder.mutate({
          shapefileNodeId: dragKey,
          moveToId: dropKey,
          dropPosition: dropPosition,
          type: "project"
        });
      } else if (dragType === "shapefile") {
        dropKey = getDropKey(dropKey);
        moveProjectShapefile.mutate({
          shapefileId: dragKey,
          moveToId: dropKey,
          dropPosition: dropPosition,
          type: "project"
        });
      } else if (dragKey === "") {
        // The user attempted to move the root shapefiles folder, so ignore.
      } else {
        toast.error("Unable to move layer");
      }
    },
    [tree]
  );

  const uploadShapefilesList = useUploadProjectShapefileList({
    visibility: "organization",
    projectId: ""
  });

  function onAcceptFiles(files, parentId) {
    // The drop reader answers with nothing when the drop held no file the upload
    // takes, and it has already said so. Starting an upload of nothing would only
    // turn the flag on and flash the spinner
    if (files.length === 0) return;
    setUploadError(false);
    uploadShapefilesList.mutate({
      files,
      shapefileNodeId: parentId
    });
  }

  const customTreeNode = (node: ProjectTreeNodeT) => {
    return (
      <NodeProviderWithManager node={node}>
        <OrganizationTreeNodeManager
          node={node}
          setSelectedShapefile={setSelectedShapefile}
        />
      </NodeProviderWithManager>
    );
  };

  return (
    <>
      {showHeader && (
        <div className="organizationAccountHeader">
          <Heading element="h4">Organization Shapefiles and GeoTIFFs</Heading>
        </div>
      )}
      <div className="org-shapefiles">
        {selectedShapefile && (
          <ShapefileDetailsWrapper>
            <OrganizationShapefileDetails
              selectedProjectId={""}
              selectedShapefile={selectedShapefile}
              shapefileFeatures={shapefileFeatures}
              selectedFeatureProperty={selectedFeatureProperty}
              setSelectedFeatureProperty={setSelectedFeatureProperty}
              setSelectedShapefile={setSelectedShapefile}
            />
          </ShapefileDetailsWrapper>
        )}
        {/* react-dropzone walks a dropped folder to any depth on its own, which
            sends far more than the user meant to drop. Reading the drop here instead
            keeps it to one level, the way the project drop modal reads it */}
        {allowUpload && (
          <Dropzone onDrop={onAcceptFiles} getFilesFromEvent={getFilesFromDropEvent}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div className="org-dropzone-container">
                <div
                  className={`dropzone ${uploadError ? "hasError" : ""} ${
                    isDragActive ? "dragActive" : ""
                  }`}
                  {...getRootProps()}>
                  <input title="Upload shapefiles and GeoTIFFs" {...getInputProps()} />
                  <p style={{ textAlign: "center" }}>
                    Drag and drop files or a folder here, or click to browse.
                    <br />
                    Shapefiles (.zip or .shp, .shx, .dbf, .prj) and GeoTIFFs (.tif, .tiff)
                  </p>
                </div>
              </div>
            )}
          </Dropzone>
        )}
        {uploadShapefilesList.isLoading && (
          <SpinnerContainer>
            <IconSpinner size={32} />
          </SpinnerContainer>
        )}
        {isLoadingColors && (
          <SpinnerContainer>
            <IconSpinner size={32} />
          </SpinnerContainer>
        )}
        {tree.length > 0 && (
          <div ref={treeContainer} className="org-tree-container">
            <Tree
              className="draggable-tree"
              selectable={false}
              showLine={{ showLeafIcon: true }}
              showIcon={true}
              onDrop={onDrop}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              draggable={(node: any) => {
                if (!isTreeDraggable) return false;

                return node.type !== "root" && isAdminOrPowerUser;
              }}
              blockNode
              defaultExpandAll={true}
              treeData={tree}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              titleRender={customTreeNode}
            />
          </div>
        )}
      </div>
    </>
  );
}

const SpinnerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: grid;
  justify-content: center;
  align-items: center;
  background: #dededec4;
  width: 100%;
  height: 100%;
  z-index: 9999;
`;

const ShapefileDetailsWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 99999;
  background-color: #fcfdfc;
`;
